import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import axiosInstance from '../../api/axiosConfig';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Try to send to API
      try {
        await axiosInstance.post('/contact/enquiry', formData);
      } catch (apiError) {
        // If API fails, log to console (mock)
        console.log('Enquiry submitted:', formData);
      }

      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError('Failed to send enquiry. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ mb: 2, fontWeight: 'bold' }}>
          Contact Us
        </Typography>
        <Typography variant="h6" sx={{ color: '#666', mb: 4 }}>
          We're here to help! Reach out to us anytime.
        </Typography>
      </Box>

      <Grid container spacing={4} sx={{ mb: 6 }}>
        {/* Contact Information */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                Contact Information
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PhoneIcon sx={{ color: '#1976d2', mr: 2 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Phone
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      +91-7979-966901
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  <EmailIcon sx={{ color: '#1976d2', mr: 2, mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Email
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      kifisrg@gmail.com
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  <LocationOnIcon sx={{ color: '#1976d2', mr: 2, mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Address
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Kavya Institute of Fire & Industrial Safety<br />
                      T-1, 2nd Floor 90, New Rajdhani Enclave <br />
                      New Delhi - 110001<br />
                      India
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', mt: 4 }}>
                Office Hours
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Monday - Friday:</strong> 9:00 AM - 6:00 PM
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Saturday:</strong> 10:00 AM - 4:00 PM
              </Typography>
              <Typography variant="body2">
                <strong>Sunday:</strong> Closed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Form */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                Send us an Enquiry
              </Typography>

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Thank you! Your enquiry has been sent successfully. We'll get back to you soon.
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Send Enquiry'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Contact Options */}
      <Box sx={{ backgroundColor: '#f5f5f5', p: 4, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center' }}>
          Quick Contact Options
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <PhoneIcon sx={{ fontSize: '2rem', color: '#1976d2', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Call Us
              </Typography>
              <Button href="tel:+917979966901">+91-7979-966901</Button>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <EmailIcon sx={{ fontSize: '2rem', color: '#1976d2', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Email Us
              </Typography>
              <Button href="mailto:kifisrg@gmail.com">
                kifisrg@gmail.com
              </Button>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ fontSize: '2rem', color: '#1976d2', mb: 1 }}>💬</Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                WhatsApp
              </Typography>
              <Button
                href="https://wa.me/917979966901"
                target="_blank"
                rel="noopener noreferrer"
              >
                Chat with us
              </Button>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <LocationOnIcon sx={{ fontSize: '2rem', color: '#1976d2', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Visit Us
              </Typography>
              <Typography variant="body2">C 201 Sector 21 Dwarka, Delhi</Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Contact;
